import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { driverAPI } from '../../api/drivers';
import { CreateDriverRequest } from '../../types/api';
import toast from 'react-hot-toast';

export const DriverForm: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateDriverRequest>({
    defaultValues: {
      status: 'ACTIVE',
    },
  });

  useEffect(() => {
    if (isEdit && id) {
      loadDriver(parseInt(id));
    }
  }, [id, isEdit]);

  const loadDriver = async (driverId: number) => {
    try {
      const driver = await driverAPI.getById(driverId);
      reset({
        name: driver.name,
        phone: driver.phone || '',
        licenseNo: driver.licenseNo || '',
        status: driver.status || 'ACTIVE',
      });
    } catch (error) {
      toast.error(t('drivers.loadError'));
      navigate('/drivers');
    }
  };

  const onSubmit = async (data: CreateDriverRequest) => {
    setLoading(true);
    try {
      if (isEdit && id) {
        await driverAPI.update(parseInt(id), data);
        toast.success(t('drivers.updateSuccess'));
      } else {
        await driverAPI.create(data);
        toast.success(t('drivers.createSuccess'));
      }
      navigate('/drivers');
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errors.unknown'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">
        {isEdit ? t('drivers.editDriver') : t('drivers.addNew')}
      </h1>

      <form onSubmit={handleSubmit(onSubmit)} className="card space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">
            {t('drivers.name')} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            {...register('name', { required: t('validation.required') })}
            className="input-field"
            placeholder={t('drivers.name')}
          />
          {errors.name && (
            <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">{t('drivers.phone')}</label>
          <input
            type="text"
            {...register('phone')}
            className="input-field"
            placeholder={t('drivers.phone')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">{t('drivers.licenseNumber')}</label>
          <input
            type="text"
            {...register('licenseNo')}
            className="input-field"
            placeholder={t('drivers.licenseNumber')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">{t('common.status')}</label>
          <select {...register('status')} className="input-field">
            <option value="ACTIVE">{t('common.active')}</option>
            <option value="INACTIVE">{t('common.inactive')}</option>
          </select>
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={() => navigate('/drivers')}
            className="btn-secondary"
          >
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={loading} className="btn-primary">
            {loading ? t('common.loading') : isEdit ? t('common.save') : t('common.add')}
          </button>
        </div>
      </form>
    </div>
  );
};
